/**
 * Cubic-bezier interpolation through an ordered list of points. Builds
 * an SVG `d` attribute that draws a smooth snake through every node
 * centre — same algorithm as the design prototype's {@code buildPath}.
 *
 * <p>For each segment, the control points are derived from the
 * neighbouring points (Catmull-Rom-ish), so the path stays smooth
 * across turns without overshooting.
 */
export function buildSvgPath(points: readonly { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  let d = `M${points[0]!.x.toFixed(1)} ${points[0]!.y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/** Lighten a hex color toward white by {@code t} (0..1). */
export function lighten(hex: string, t: number): string {
  const h = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((h >> 16) & 255) + 255 * t);
  const g = Math.min(255, ((h >> 8) & 255) + 255 * t);
  const b = Math.min(255, (h & 255) + 255 * t);
  return `rgb(${r | 0},${g | 0},${b | 0})`;
}

/** Darken a hex color toward black by {@code t} (0..1). */
export function darken(hex: string, t: number): string {
  const h = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((h >> 16) & 255) - 255 * t);
  const g = Math.max(0, ((h >> 8) & 255) - 255 * t);
  const b = Math.max(0, (h & 255) - 255 * t);
  return `rgb(${r | 0},${g | 0},${b | 0})`;
}
